import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, MapPin } from "lucide-react";
import { MediaImage } from "../MediaImage";
import { useSiteMedia } from "../../lib/siteMedia";
import { api, imageUrl } from "../../lib/api";
import { INSPIRATION } from "../../data/homeContent";
import { SectionHeading } from "./CtaButtons";

// Alleen projecten met via beheer geüploade foto's (geen externe stockbeelden) tonen als uitgevoerd werk.
const isRealProject = (p) => p.hero_image?.path && !/^https?:\/\//i.test(p.hero_image.path);

const InspirationCard = ({ item, media, large }) => (
  <li className={`group relative overflow-hidden rounded-2xl ${large ? "lg:col-span-7 lg:row-span-2" : "lg:col-span-5"}`} data-testid={`inspiration-${item.media}`}>
    <MediaImage media={media} badgeTop sizes={large ? "(min-width: 1024px) 700px, 100vw" : "(min-width: 1024px) 500px, 100vw"}
      className={`aspect-[4/3] ${large ? "lg:aspect-auto lg:h-full" : "lg:aspect-[16/10]"}`} imgClassName="transition-transform duration-700 group-hover:scale-[1.03]" />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F2537]/90 via-[#0F2537]/35 to-transparent" aria-hidden="true" />
    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
      <h3 className="font-display text-[1.25rem] font-bold text-white sm:text-[1.4rem]">{item.title}</h3>
      <p className="mt-1.5 max-w-md text-[14.5px] leading-relaxed text-slate-200">{item.text}</p>
      <Link to={item.href} className="mt-3 inline-flex items-center gap-1.5 text-[14.5px] font-semibold text-white underline-offset-4 hover:underline">
        {item.cta} <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  </li>
);

const RealProjects = ({ projects }) => (
  <div className="mt-16" data-testid="real-projects">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h3 className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink"><BadgeCheck className="h-4 w-4 text-maas" aria-hidden="true" /> Uitgevoerd door Maas Kozijn</h3>
      <Link to="/projecten" className="inline-flex items-center gap-1.5 text-[14.5px] font-semibold text-maas hover:underline" data-testid="real-projects-all">Alle projecten <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
    </div>
    <ul className="mt-5 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {projects.map((p) => (
        <li key={p.id} className="card card-hover overflow-hidden">
          <Link to={`/projecten/${p.slug}`} data-testid={`real-project-${p.slug}`}>
            <img src={imageUrl(p.hero_image.path)} alt={p.hero_image.alt || p.title} loading="lazy" className="aspect-[4/3] w-full object-cover" />
            <div className="p-5">
              {p.plaats && <p className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-gold"><MapPin className="h-3.5 w-3.5" aria-hidden="true" /> {p.plaats}</p>}
              <p className="mt-1.5 font-semibold text-ink">{p.title}</p>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export const Inspiration = () => {
  const media = useSiteMedia();
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects((r.data || []).filter(isRealProject).slice(0, 3))).catch(() => setProjects([]));
  }, []);
  return (
    <section aria-labelledby="inspiratie-title" className="section" data-testid="home-inspiration">
      <div className="container-tight">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <SectionHeading id="inspiratie-title" eyebrow="Inspiratie" title="Ideeën voor uw woning"
            lead="Sfeerbeelden van mogelijke uitvoeringen, ter inspiratie. Het zijn geen door Maas Kozijn uitgevoerde projecten." />
          <Link to="/projecten/inspiratie" className="inline-flex shrink-0 items-center gap-1.5 text-[15px] font-semibold text-maas hover:underline" data-testid="home-inspiration-more">
            Meer inspiratie <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
        <ul className="mt-10 grid gap-6 lg:grid-cols-12 lg:grid-rows-2">
          {INSPIRATION.map((item, i) => <InspirationCard key={item.media} item={item} media={media(item.media)} large={i === 0} />)}
        </ul>
        {projects.length > 0 && <RealProjects projects={projects} />}
      </div>
    </section>
  );
};
